import { motion, AnimatePresence } from "framer-motion";
import { useMemo, useState } from "react";

import {
  FaArrowDown,
  FaArrowRight,
  FaBolt,
  FaCheckCircle,
  FaGraduationCap,
  FaGlobe,
  FaLaptopCode,
  FaShieldAlt,
  FaBrain,
  FaDatabase,
  FaRobot,
  FaTimes,
  FaPaperPlane,
  FaWhatsapp,
  FaLightbulb,
  FaClock,
  FaUserGraduate,
} from "react-icons/fa";

import Layout from "../../components/shared/layout/layout";
import AllCourses from "./components/software-engineering";


const Courses = () => {

  /* =========================================================
      PROGRAMME ADVISOR STATE
  ========================================================== */

  const [showAdvisor, setShowAdvisor] = useState(false);

  const [advisorStep, setAdvisorStep] = useState(0);

  const [answers, setAnswers] = useState({
    goal: "",
    experience: "",
    interest: "",
    time: "",
  });

  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hi 👋 I'm Zulfah's Programme Advisor.",
    },
    {
      sender: "bot",
      text: "I'll ask you a few quick questions and recommend the programme that fits your goals, experience and interests.",
    },
  ]);


  /* =========================================================
      PROGRAMME DATA
  ========================================================== */

  const disciplines = [
    {
      icon: FaLaptopCode,
      title: "Software Engineering",
      color: "#59b5cd",
    },
    {
      icon: FaShieldAlt,
      title: "Cybersecurity",
      color: "#ecb32c",
    },
    {
      icon: FaBrain,
      title: "Artificial Intelligence",
      color: "#59b5cd",
    },
    {
      icon: FaDatabase,
      title: "Data & Analytics",
      color: "#ecb32c",
    },
  ];


  /* =========================================================
      ADVISOR QUESTIONS
  ========================================================== */

  const questions = [
    {
      key: "goal",
      question: "First, what are you hoping to achieve? 🎯",
      options: [
        "Become a software developer",
        "Start a career in cybersecurity",
        "Work in AI",
        "Become a data professional",
        "I'm not sure yet",
      ],
    },

    {
      key: "experience",
      question: "How would you describe your current tech experience?",
      options: [
        "I'm completely new",
        "I've tried a few things",
        "I have some experience",
        "I'm already working in tech",
      ],
    },

    {
      key: "interest",
      question: "What sounds most exciting to you?",
      options: [
        "Building apps & websites",
        "Protecting systems & people",
        "Building intelligent systems",
        "Working with data & insights",
        "I want to explore technology",
      ],
    },

    {
      key: "time",
      question: "Finally, how much time can you realistically commit?",
      options: [
        "A few hours per week",
        "Weekends",
        "Weekdays",
        "I'm ready to go all in",
      ],
    },
  ];


  /* =========================================================
      START ADVISOR
  ========================================================== */

  const openAdvisor = () => {
    setShowAdvisor(true);

    setAdvisorStep(0);

    setAnswers({
      goal: "",
      experience: "",
      interest: "",
      time: "",
    });

    setMessages([
      {
        sender: "bot",
        text: "Hi 👋 I'm Zulfah's Programme Advisor.",
      },
      {
        sender: "bot",
        text: "I'll ask you a few quick questions and recommend the programme that fits your goals, experience and interests.",
      },
    ]);
  };


  /* =========================================================
      HANDLE ANSWERS
  ========================================================== */

  const handleAdvisorAnswer = (answer) => {

    const currentQuestion = questions[advisorStep];

    const updatedAnswers = {
      ...answers,
      [currentQuestion.key]: answer,
    };

    setAnswers(updatedAnswers);

    // Add user's response
    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        text: answer,
      },
    ]);


    // If there are more questions
    if (advisorStep < questions.length - 1) {

      const nextStep = advisorStep + 1;

      setAdvisorStep(nextStep);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: questions[nextStep].question,
          },
        ]);
      }, 350);

    } else {

      // Finished
      setAdvisorStep(questions.length);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            sender: "bot",
            text: "Perfect. I've got enough information. Let me analyse your answers and find your best path 🚀",
          },
        ]);
      }, 350);
    }
  };


  /* =========================================================
      RECOMMENDATION ENGINE
  ========================================================== */

  const recommendation = useMemo(() => {

    if (advisorStep < questions.length) {
      return null;
    }

    const {
      goal,
      experience,
      interest,
    } = answers;

    let programme = "Software Engineering";

    let reason =
      "Software Engineering gives you a strong foundation for building real digital products and opens multiple career paths in technology.";

    let icon = FaLaptopCode;

    let color = "#59b5cd";


    // AI
    if (
      goal.includes("AI") ||
      interest.includes("intelligent")
    ) {
      programme = "Artificial Intelligence";

      reason =
        "Your interest in intelligent systems makes Artificial Intelligence a strong fit. You'll explore how technology can learn, reason and solve real-world problems.";

      icon = FaBrain;
      color = "#59b5cd";
    }


    // Cybersecurity
    else if (
      goal.includes("cybersecurity") ||
      interest.includes("Protecting")
    ) {
      programme = "Cybersecurity";

      reason =
        "You appear naturally drawn to security and protecting systems. Cybersecurity is built around understanding vulnerabilities, defending infrastructure and thinking like an attacker and defender.";

      icon = FaShieldAlt;
      color = "#ecb32c";
    }


    // Data
    else if (
      goal.includes("data") ||
      interest.includes("data")
    ) {
      programme = "Data & Analytics";

      reason =
        "Your interest in data makes this a strong match. You'll learn how to turn raw information into insights that support smarter business and technical decisions.";

      icon = FaDatabase;
      color = "#ecb32c";
    }


    // Software
    else if (
      goal.includes("software") ||
      interest.includes("apps")
    ) {
      programme = "Software Engineering";

      reason =
        "You want to build things. Software Engineering is the strongest starting point for learning how to design, develop and ship real applications.";

      icon = FaLaptopCode;
      color = "#59b5cd";
    }


    // Beginner fallback
    if (
      experience === "I'm completely new" &&
      goal === "I'm not sure yet"
    ) {
      programme = "Software Engineering";

      reason =
        "Since you're still exploring technology, Software Engineering is a great starting point. It gives you a broad foundation and lets you discover what area of tech excites you most.";

      icon = FaLaptopCode;
      color = "#59b5cd";
    }


    return {
      programme,
      reason,
      icon,
      color,
    };

  }, [answers, advisorStep]);


  /* =========================================================
      WHATSAPP
  ========================================================== */

  const goToWhatsApp = () => {

    const message = `
Hi Zulfah 👋

I just used the Zulfah Programme Advisor.

My recommended programme is:
${recommendation?.programme}

My responses:
🎯 Goal: ${answers.goal}
💻 Experience: ${answers.experience}
💡 Interest: ${answers.interest}
⏰ Availability: ${answers.time}

I'd like to speak with an advisor about enrolling.
    `.trim();

    const whatsappUrl =
      `https://wa.me/2348084259372?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, "_blank");
  };


  /* =========================================================
      HIGHLIGHTS
  ========================================================== */

  const highlights = [
    {
      icon: FaLaptopCode,
      title: "Learn by Building",
      text: "Real projects. Real tools. Real-world engineering experience.",
    },
    {
      icon: FaGraduationCap,
      title: "Industry-Focused",
      text: "Curricula designed around skills modern employers actually need.",
    },
    {
      icon: FaGlobe,
      title: "Built for Global Careers",
      text: "Develop skills that can open opportunities beyond your location.",
    },
  ];


  return (
    <Layout>

      {/* =========================================================
          HERO
      ========================================================== */}

      <section className="relative overflow-hidden bg-[#071014] text-white">

        {/* Ambient background */}

        <div className="absolute inset-0 pointer-events-none">

          <div
            className="
              absolute
              -top-40
              -left-40
              h-[550px]
              w-[550px]
              rounded-full
              bg-[#59b5cd]/10
              blur-[140px]
            "
          />

          <div
            className="
              absolute
              top-20
              -right-40
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#ecb32c]/10
              blur-[140px]
            "
          />

          <div
            className="
              absolute
              inset-0
              opacity-[0.035]
              bg-[linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)]
              bg-[size:60px_60px]
            "
          />

        </div>


        <div className="relative z-10 max-w-7xl mx-auto px-6">

          <div className="min-h-[650px] flex items-center py-24">

            <div className="grid lg:grid-cols-[1.15fr_.85fr] gap-16 items-center w-full">

              {/* LEFT */}

              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >

                <div className="flex items-center gap-3 mb-7">

                  <span className="h-[1px] w-10 bg-[#ecb32c]" />

                  <span className="text-xs md:text-sm uppercase tracking-[0.25em] font-bold text-[#ecb32c]">
                    Zulfah Academy
                  </span>

                </div>


                <h1
                  className="
                    text-5xl
                    sm:text-6xl
                    lg:text-7xl
                    font-black
                    leading-[0.98]
                    tracking-tight
                  "
                >
                  Don't just
                  <br />

                  <span className="text-white">
                    learn tech.
                  </span>

                  <br />

                  <span
                    className="
                      bg-gradient-to-r
                      from-[#59b5cd]
                      via-[#59b5cd]
                      to-[#ecb32c]
                      bg-clip-text
                      text-transparent
                    "
                  >
                    Become tech.
                  </span>

                </h1>


                <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/55 leading-relaxed">
                  Build practical, career-ready skills in software engineering,
                  cybersecurity, artificial intelligence, data and more —
                  through immersive training designed for the real world.
                </p>


                {/* CTA */}

                <div className="flex flex-wrap gap-4 mt-9">

                  <a
                    href="#programmes"
                    className="
                      inline-flex
                      items-center
                      gap-3
                      rounded-full
                      bg-[#ecb32c]
                      px-7
                      py-4
                      font-bold
                      text-[#071014]
                      transition
                      hover:scale-105
                      hover:shadow-[0_0_35px_rgba(236,179,44,.25)]
                    "
                  >
                    Explore Programmes
                    <FaArrowRight className="text-xs" />
                  </a>


                  {/* =================================================
                      AI ADVISOR BUTTON
                  ================================================= */}

                  <button
                    onClick={openAdvisor}
                    className="
                      group
                      inline-flex
                      items-center
                      gap-3
                      rounded-full
                      border
                      border-white/15
                      bg-white/[0.06]
                      px-7
                      py-4
                      font-semibold
                      text-white
                      backdrop-blur-xl
                      transition-all
                      duration-300
                      hover:bg-white/10
                      hover:border-white/30
                      hover:-translate-y-1
                    "
                  >

                    <span
                      className="
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        bg-[#59b5cd]
                        text-sm
                        shadow-lg
                        shadow-[#59b5cd]/30
                      "
                    >
                      <FaRobot />
                    </span>

                  Talk to an Advisor

                    <span className="transition-transform group-hover:translate-x-1">
                      →
                    </span>

                  </button>

                </div>


                {/* Trust */}

                <div className="flex flex-wrap gap-7 mt-12">

                  <div>
                    <p className="text-3xl font-black text-[#59b5cd]">
                      500+
                    </p>

                    <p className="text-xs text-white/40 mt-1">
                      Learners trained
                    </p>
                  </div>


                  <div className="h-12 w-px bg-white/10" />


                  <div>
                    <p className="text-3xl font-black text-[#ecb32c]">
                      10+
                    </p>

                    <p className="text-xs text-white/40 mt-1">
                      Countries reached
                    </p>
                  </div>


                  <div className="h-12 w-px bg-white/10" />


                  <div>
                    <p className="text-3xl font-black text-white">
                      100%
                    </p>

                    <p className="text-xs text-white/40 mt-1">
                      Practical mindset
                    </p>
                  </div>

                </div>

              </motion.div>


              {/* RIGHT VISUAL */}

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.9,
                  delay: 0.2,
                }}
                className="relative hidden lg:block"
              >

                <div
                  className="
                    relative
                    rounded-[32px]
                    border
                    border-white/10
                    bg-white/[0.045]
                    p-7
                    backdrop-blur-xl
                    shadow-2xl
                  "
                >

                  <div className="flex justify-between items-center mb-8">

                    <div>

                      <p className="text-xs uppercase tracking-[0.2em] text-white/30">
                        Learning ecosystem
                      </p>

                      <h3 className="text-2xl font-bold mt-2">
                        Choose your path.
                      </h3>

                    </div>


                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#ecb32c]/10 text-[#ecb32c]">
                      <FaBolt />
                    </div>

                  </div>


                  <div className="space-y-3">

                    {disciplines.map((item, index) => {

                      const Icon = item.icon;

                      return (

                        <motion.div
                          key={item.title}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: 0.5 + index * 0.12,
                          }}
                          className="
                            group
                            flex
                            items-center
                            gap-4
                            rounded-2xl
                            border
                            border-white/10
                            bg-white/[0.025]
                            p-4
                            transition
                            hover:bg-white/[0.07]
                            hover:border-white/20
                          "
                        >

                          <div
                            className="flex h-12 w-12 items-center justify-center rounded-xl"
                            style={{
                              backgroundColor: `${item.color}12`,
                              color: item.color,
                            }}
                          >
                            <Icon />
                          </div>


                          <div className="flex-1">

                            <p className="font-semibold">
                              {item.title}
                            </p>

                            <p className="text-xs text-white/35 mt-1">
                              Career-focused programme
                            </p>

                          </div>


                          <FaArrowRight className="text-xs text-white/20 group-hover:text-[#ecb32c] group-hover:translate-x-1 transition" />

                        </motion.div>

                      );

                    })}

                  </div>


                  <div className="mt-7 rounded-2xl bg-gradient-to-r from-[#59b5cd]/10 to-[#ecb32c]/10 border border-white/5 p-5">

                    <div className="flex items-center gap-3">

                      <FaCheckCircle className="text-[#59b5cd]" />

                      <p className="text-sm text-white/60">
                        Beginner-friendly pathways available
                      </p>

                    </div>

                  </div>

                </div>


                {/* Floating card */}

                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    absolute
                    -bottom-8
                    -left-8
                    rounded-2xl
                    border
                    border-white/10
                    bg-[#101a1e]/90
                    px-5
                    py-4
                    shadow-2xl
                    backdrop-blur-xl
                  "
                >

                  <div className="flex items-center gap-3">

                    <div className="h-10 w-10 rounded-full bg-[#59b5cd]/10 flex items-center justify-center text-[#59b5cd]">
                      <FaGraduationCap />
                    </div>

                    <div>

                      <p className="text-sm font-bold">
                        Learn. Build. Launch.
                      </p>

                      <p className="text-xs text-white/35">
                        The Zulfah way.
                      </p>

                    </div>

                  </div>

                </motion.div>

              </motion.div>

            </div>

          </div>


          {/* Scroll */}

          <motion.div
            animate={{ y: [0, 7, 0] }}
            transition={{
              duration: 1.8,
              repeat: Infinity,
            }}
            className="
              pb-10
              flex
              flex-col
              items-center
              gap-3
              text-white/30
            "
          >

            <span className="text-[10px] uppercase tracking-[0.3em]">
              Explore
            </span>

            <FaArrowDown className="text-xs" />

          </motion.div>

        </div>

      </section>


      {/* =========================================================
          COURSES
      ========================================================== */}

      <section
        id="programmes"
        className="relative bg-[#f7fafb] py-1"
      >

        <AllCourses />

      </section>


      {/* =========================================================
          FINAL CTA
      ========================================================== */}

      <section className="relative overflow-hidden bg-[#071014] py-28 text-white">

        <div className="absolute inset-0 pointer-events-none">

          <div className="absolute left-1/4 -top-40 h-[400px] w-[400px] rounded-full bg-[#59b5cd]/10 blur-[130px]" />

          <div className="absolute right-1/4 -bottom-40 h-[400px] w-[400px] rounded-full bg-[#ecb32c]/10 blur-[130px]" />

        </div>


        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto px-6 text-center"
        >

          <p className="text-[#ecb32c] text-xs uppercase tracking-[0.3em] font-bold">
            Your next chapter
          </p>


          <h2 className="mt-5 text-4xl md:text-6xl font-black leading-tight">

            The world doesn't need

            <br />

            <span
              className="
                bg-gradient-to-r
                from-[#59b5cd]
                to-[#ecb32c]
                bg-clip-text
                text-transparent
              "
            >
              more certificates.
            </span>

            <br />

            It needs builders.

          </h2>


          <p className="mt-7 text-white/45 max-w-2xl mx-auto text-lg leading-relaxed">

            Choose a programme, develop real skills, build meaningful projects
            and take your first step toward becoming a technology professional.

          </p>


          <div className="mt-9 flex justify-center">

            <button
              onClick={openAdvisor}
              className="
                inline-flex
                items-center
                gap-3
                rounded-full
                bg-[#ecb32c]
                px-8
                py-4
                font-bold
                text-[#071014]
                transition
                hover:scale-105
                hover:shadow-[0_0_40px_rgba(236,179,44,.25)]
              "
            >

              <FaRobot />

              Find My Programme

              <FaArrowRight className="text-xs" />

            </button>

          </div>

        </motion.div>

      </section>


      {/* =========================================================
          AI PROGRAMME ADVISOR
      ========================================================== */}

<AnimatePresence>
  {showAdvisor && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={() => setShowAdvisor(false)}
      className="
        fixed inset-0 z-[9999]
        flex items-center justify-center
        overflow-hidden
        bg-[#02090c]/90
        p-0 sm:p-4 lg:p-6
        backdrop-blur-2xl
      "
    >

      {/* =========================================================
          AMBIENT BACKGROUND
      ========================================================= */}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">

        {/* Main cyan orb */}
        <motion.div
          animate={{
            x: [0, 100, -30, 0],
            y: [0, -60, 40, 0],
            scale: [1, 1.15, 0.95, 1],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            absolute -left-40 -top-40
            h-[600px] w-[600px]
            rounded-full
            bg-[#59b5cd]/20
            blur-[150px]
          "
        />

        {/* Gold orb */}
        <motion.div
          animate={{
            x: [0, -80, 40, 0],
            y: [0, 70, -30, 0],
            scale: [1, 1.1, 0.9, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            absolute -bottom-48 -right-40
            h-[600px] w-[600px]
            rounded-full
            bg-[#ecb32c]/16
            blur-[150px]
          "
        />

        {/* Pink orb */}
        <motion.div
          animate={{
            x: [0, 50, -50, 0],
            y: [0, -40, 40, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="
            absolute left-1/2 top-1/2
            h-[400px] w-[400px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[#CF385D]/10
            blur-[140px]
          "
        />

        {/* Grid */}
        <div
          className="
            absolute inset-0
            opacity-[0.035]
            [background-image:linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)]
            [background-size:55px_55px]
          "
        />

        {/* Radial rings */}
        <div
          className="
            absolute left-1/2 top-1/2
            h-[900px] w-[900px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            border border-white/[0.035]
          "
        />

        <div
          className="
            absolute left-1/2 top-1/2
            h-[650px] w-[650px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            border border-white/[0.035]
          "
        />

        <div
          className="
            absolute left-1/2 top-1/2
            h-[420px] w-[420px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            border border-white/[0.025]
          "
        />

        {/* Floating particles */}
        {[
          ["left-[12%]", "top-[18%]", "delay-0"],
          ["left-[84%]", "top-[22%]", "delay-700"],
          ["left-[18%]", "top-[76%]", "delay-300"],
          ["left-[78%]", "top-[82%]", "delay-1000"],
          ["left-[50%]", "top-[10%]", "delay-500"],
          ["left-[92%]", "top-[55%]", "delay-200"],
        ].map(([position, top, delay], index) => (
          <motion.span
            key={index}
            animate={{
              y: [0, -18, 0],
              opacity: [0.2, 0.7, 0.2],
              scale: [0.8, 1.2, 0.8],
            }}
            transition={{
              duration: 4 + index,
              repeat: Infinity,
              delay: index * 0.4,
              ease: "easeInOut",
            }}
            className={`
              absolute
              ${position}
              ${top}
              h-1.5
              w-1.5
              rounded-full
              bg-[#59b5cd]
              shadow-[0_0_15px_rgba(89,181,205,.8)]
            `}
          />
        ))}

      </div>


      {/* =========================================================
          MAIN WINDOW
      ========================================================= */}

      <motion.div
        initial={{
          opacity: 0,
          scale: 0.94,
          y: 35,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          scale: 0.94,
          y: 35,
        }}
        transition={{
          type: "spring",
          stiffness: 180,
          damping: 22,
        }}
        onClick={(e) => e.stopPropagation()}
        className="
          relative
          flex
          h-[100dvh]
          w-full
          max-w-[920px]
          flex-col
          overflow-hidden
          rounded-none
          border-0
          bg-[#f7faf9]
          shadow-[0_50px_160px_rgba(0,0,0,.65)]
          sm:h-[94vh]
          sm:rounded-[34px]
          sm:border
          sm:border-white/20
        "
      >

        {/* =====================================================
            TOP ENERGY LINE
        ===================================================== */}

        <div
          className="
            absolute left-0 right-0 top-0 z-[100]
            h-[3px]
            bg-gradient-to-r
            from-[#59b5cd]
            via-[#ecb32c]
            to-[#CF385D]
          "
        />

        {/* =====================================================
            HEADER
        ===================================================== */}

        <header
          className="
            relative
            shrink-0
            overflow-hidden
            border-b border-black/[0.05]
            bg-white
            px-5
            pb-5
            pt-6
            sm:px-8
            sm:pt-7
          "
        >

          {/* Header glow */}
          <div
            className="
              pointer-events-none
              absolute
              -right-32
              -top-40
              h-[350px]
              w-[350px]
              rounded-full
              bg-[#59b5cd]/15
              blur-[100px]
            "
          />

          <div
            className="
              pointer-events-none
              absolute
              bottom-[-100px]
              left-[30%]
              h-[250px]
              w-[250px]
              rounded-full
              bg-[#ecb32c]/10
              blur-[90px]
            "
          />

          <div className="relative flex items-center justify-between">

            {/* BRAND */}
            <div className="flex items-center gap-3.5 sm:gap-4">

              {/* AI CORE */}
              <div className="relative">

                {/* orbit */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="
                    absolute
                    -inset-2
                    rounded-[24px]
                    border
                    border-dashed
                    border-[#59b5cd]/25
                  "
                />

                <motion.div
                  animate={{
                    y: [0, -4, 0],
                    rotate: [0, 2, -2, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    relative
                    flex
                    h-14
                    w-14
                    items-center
                    justify-center
                    overflow-hidden
                    rounded-[19px]
                    bg-gradient-to-br
                    from-[#63c4dc]
                    via-[#459fb8]
                    to-[#286f84]
                    text-white
                    shadow-[0_18px_40px_rgba(89,181,205,.35)]
                    sm:h-16
                    sm:w-16
                  "
                >

                  {/* inner shine */}
                  <div
                    className="
                      absolute inset-0
                      bg-gradient-to-br
                      from-white/25
                      via-transparent
                      to-transparent
                    "
                  />

                  <FaRobot className="relative z-10 text-xl sm:text-2xl" />

                </motion.div>

                {/* online */}
                <span
                  className="
                    absolute
                    -right-1
                    -top-1
                    z-20
                    h-4
                    w-4
                    rounded-full
                    border-[3px]
                    border-white
                    bg-green-400
                    shadow-[0_0_12px_rgba(74,222,128,.7)]
                  "
                />

              </div>


              {/* TITLE */}
              <div>

                <div className="flex items-center gap-2">

                  <h2
                    className="
                      font-[ubuntu]
                      text-lg
                      font-black
                      tracking-tight
                      text-gray-950
                      sm:text-xl
                    "
                  >
                    Zulfah Advisor
                  </h2>

                  <span
                    className="
                      rounded-full
                      border
                      border-[#59b5cd]/15
                      bg-[#59b5cd]/10
                      px-2
                      py-1
                      text-[7px]
                      font-black
                      uppercase
                      tracking-[0.2em]
                      text-[#398aa1]
                      sm:px-2.5
                    "
                  >
                    AI
                  </span>

                </div>

                <div className="mt-1.5 flex items-center gap-2">

                  <span className="relative flex h-2 w-2">

                    <span
                      className="
                        absolute
                        h-full
                        w-full
                        animate-ping
                        rounded-full
                        bg-green-400
                        opacity-70
                      "
                    />

                    <span
                      className="
                        relative
                        h-2
                        w-2
                        rounded-full
                        bg-green-500
                      "
                    />

                  </span>

                  <p className="text-[10px] text-gray-400 sm:text-xs">
                    Your personal programme guide
                  </p>

                </div>

              </div>

            </div>


            {/* CLOSE */}
            <motion.button
              whileHover={{
                rotate: 90,
                scale: 1.08,
              }}
              whileTap={{
                scale: 0.9,
              }}
              type="button"
              onClick={() => setShowAdvisor(false)}
              className="
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-full
                border
                border-black/[0.06]
                bg-gray-50
                text-gray-400
                transition
                hover:bg-gray-950
                hover:text-white
              "
            >
              <FaTimes className="text-xs" />
            </motion.button>

          </div>


          {/* =====================================================
              INTRO CARD
          ===================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 12,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              delay: 0.18,
              duration: 0.45,
            }}
            className="
              relative
              mt-5
              overflow-hidden
              rounded-[22px]
              border
              border-[#59b5cd]/10
              bg-gradient-to-br
              from-[#59b5cd]/[0.08]
              via-white
              to-[#ecb32c]/[0.08]
              p-4
              sm:p-5
            "
          >

            <div
              className="
                absolute
                bottom-0
                left-0
                top-0
                w-1
                bg-gradient-to-b
                from-[#59b5cd]
                via-[#ecb32c]
                to-[#CF385D]
              "
            />

            <div className="flex items-center gap-3 pl-2">

              <motion.div
                animate={{
                  rotate: [0, -5, 5, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                }}
                className="
                  flex
                  h-9
                  w-9
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  bg-white
                  text-[#ecb32c]
                  shadow-[0_5px_18px_rgba(0,0,0,.06)]
                "
              >
                <FaLightbulb className="text-xs" />
              </motion.div>

              <p className="text-xs leading-5 text-gray-500 sm:text-sm sm:leading-6">
                Let's discover the Zulfah programme that best fits your
                <span className="font-bold text-gray-900">
                  {" "}goals, experience and ambitions.
                </span>
              </p>

            </div>

          </motion.div>

        </header>


        {/* =========================================================
            JOURNEY PROGRESS
        ========================================================= */}

        <div className="shrink-0 bg-white px-5 pb-4 pt-4 sm:px-8">

          <div className="mb-2.5 flex items-center justify-between">

            <div className="flex items-center gap-2">

              <span
                className="
                  text-[8px]
                  font-black
                  uppercase
                  tracking-[0.25em]
                  text-gray-400
                "
              >
                Your journey
              </span>

              <span className="h-1 w-1 rounded-full bg-gray-300" />

              <span className="hidden text-[9px] text-gray-400 sm:block">
                Finding your perfect fit
              </span>

            </div>

            <span
              className="
                rounded-full
                border border-black/[0.04]
                bg-gray-50
                px-2.5
                py-1
                font-[ubuntu]
                text-[9px]
                font-bold
                text-gray-500
              "
            >
              {Math.min(advisorStep, questions.length)}
              <span className="mx-1 text-gray-300">/</span>
              {questions.length}
            </span>

          </div>

          <div className="flex gap-1.5">

            {questions.map((_, index) => (

              <div
                key={index}
                className="
                  relative
                  h-1.5
                  flex-1
                  overflow-hidden
                  rounded-full
                  bg-gray-100
                "
              >

                <motion.div
                  animate={{
                    width:
                      index < advisorStep
                        ? "100%"
                        : index === advisorStep
                        ? "55%"
                        : "0%",
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeOut",
                  }}
                  className="
                    h-full
                    rounded-full
                    bg-gradient-to-r
                    from-[#59b5cd]
                    via-[#ecb32c]
                    to-[#CF385D]
                  "
                />

              </div>

            ))}

          </div>

        </div>


        {/* =========================================================
            CHAT AREA
        ========================================================= */}

        <div
          className="
            min-h-0
            flex-1
            overflow-y-auto
            bg-[#f7faf9]
            px-5
            py-5
            sm:px-8
            sm:py-6
          "
        >

          <div className="mx-auto w-full max-w-[700px] space-y-4">

            {messages.map((message, index) => (

              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 14,
                  scale: 0.97,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  duration: 0.35,
                  delay: index === messages.length - 1 ? 0.05 : 0,
                }}
                className={`flex ${
                  message.sender === "user"
                    ? "justify-end"
                    : "justify-start"
                }`}
              >

                {/* AI avatar */}
                {message.sender !== "user" && (
                  <div
                    className="
                      mr-2
                      mt-1
                      flex
                      h-7
                      w-7
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      bg-[#59b5cd]/10
                      text-[#398ea5]
                    "
                  >
                    <FaRobot className="text-[10px]" />
                  </div>
                )}

                <div
                  className={`
                    relative
                    max-w-[84%]
                    px-4
                    py-3.5
                    text-sm
                    leading-6
                    sm:max-w-[78%]
                    sm:px-5
                    ${
                      message.sender === "user"
                        ? `
                          rounded-[22px]
                          rounded-br-md
                          bg-gradient-to-br
                          from-[#59b5cd]
                          to-[#398ea5]
                          text-white
                          shadow-[0_12px_35px_rgba(89,181,205,.20)]
                        `
                        : `
                          rounded-[22px]
                          rounded-bl-md
                          border
                          border-black/[0.04]
                          bg-white
                          text-gray-600
                          shadow-[0_7px_25px_rgba(0,0,0,.04)]
                        `
                    }
                  `}
                >
                  {message.text}
                </div>

              </motion.div>

            ))}


            {/* =====================================================
                RECOMMENDATION
            ===================================================== */}

            {recommendation && (

              <motion.div
                initial={{
                  opacity: 0,
                  y: 30,
                  scale: 0.96,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  type: "spring",
                  stiffness: 180,
                  damping: 20,
                }}
                className="
                  relative
                  mt-6
                  overflow-hidden
                  rounded-[30px]
                  border
                  border-black/[0.05]
                  bg-white
                  shadow-[0_25px_80px_rgba(0,0,0,.09)]
                "
              >

                {/* Animated top line */}
                <motion.div
                  animate={{
                    backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="
                    h-1.5
                    bg-[length:200%_200%]
                    bg-gradient-to-r
                    from-[#59b5cd]
                    via-[#ecb32c]
                    to-[#CF385D]
                  "
                />

                <div className="p-5 sm:p-7">

                  {/* Label */}
                  <div className="flex items-center justify-between">

                    <div className="flex items-center gap-2">

                      <motion.div
                        animate={{
                          scale: [1, 1.08, 1],
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                        }}
                        className="
                          flex
                          h-9
                          w-9
                          items-center
                          justify-center
                          rounded-xl
                          bg-[#ecb32c]/10
                          text-[#d39c17]
                        "
                      >
                        <FaLightbulb className="text-xs" />
                      </motion.div>

                      <span
                        className="
                          text-[8px]
                          font-black
                          uppercase
                          tracking-[0.25em]
                          text-gray-400
                        "
                      >
                        Your Zulfah Path
                      </span>

                    </div>

                    <span
                      className="
                        flex
                        items-center
                        gap-1.5
                        rounded-full
                        border
                        border-green-100
                        bg-green-50
                        px-3
                        py-1.5
                        text-[8px]
                        font-black
                        uppercase
                        tracking-wider
                        text-green-600
                      "
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                      Strong Match
                    </span>

                  </div>


                  {/* PROGRAMME */}
                  <div className="mt-6 flex items-center gap-4">

                    <motion.div
                      initial={{
                        scale: 0.8,
                        rotate: -8,
                      }}
                      animate={{
                        scale: 1,
                        rotate: 0,
                      }}
                      transition={{
                        delay: 0.15,
                        type: "spring",
                      }}
                      className="
                        relative
                        flex
                        h-16
                        w-16
                        shrink-0
                        items-center
                        justify-center
                        overflow-hidden
                        rounded-[21px]
                      "
                      style={{
                        backgroundColor:
                          `${recommendation.color}12`,
                        color:
                          recommendation.color,
                      }}
                    >

                      <div
                        className="
                          absolute
                          inset-0
                          bg-gradient-to-br
                          from-white/50
                          to-transparent
                        "
                      />

                      {(() => {
                        const Icon = recommendation.icon;

                        return (
                          <Icon className="relative text-2xl" />
                        );
                      })()}

                    </motion.div>

                    <div>

                      <p
                        className="
                          text-[8px]
                          font-black
                          uppercase
                          tracking-[0.2em]
                          text-gray-400
                        "
                      >
                        Recommended programme
                      </p>

                      <h3
                        className="
                          mt-1
                          font-[ubuntu]
                          text-xl
                          font-black
                          tracking-tight
                          text-gray-950
                          sm:text-2xl
                        "
                      >
                        {recommendation.programme}
                      </h3>

                    </div>

                  </div>


                  {/* REASON */}
                  <div
                    className="
                      relative
                      mt-5
                      overflow-hidden
                      rounded-2xl
                      border
                      border-black/[0.03]
                      bg-[#F7F7F5]
                      p-4
                    "
                  >

                    <div
                      className="
                        absolute
                        left-0
                        top-0
                        h-full
                        w-1
                        bg-gradient-to-b
                        from-[#59b5cd]
                        to-[#ecb32c]
                      "
                    />

                    <p className="pl-1 text-sm leading-6 text-gray-500">
                      {recommendation.reason}
                    </p>

                  </div>


                  {/* MATCH SCORE */}
                  <div className="mt-5">

                    <div className="mb-2 flex items-center justify-between">

                      <span className="text-[9px] text-gray-400">
                        Programme compatibility
                      </span>

                      <motion.span
                        initial={{
                          opacity: 0,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        transition={{
                          delay: 0.5,
                        }}
                        className="
                          font-[ubuntu]
                          text-[11px]
                          font-black
                          text-[#398ea5]
                        "
                      >
                        91%
                      </motion.span>

                    </div>

                    <div
                      className="
                        h-2
                        overflow-hidden
                        rounded-full
                        bg-gray-100
                      "
                    >

                      <motion.div
                        initial={{
                          width: 0,
                        }}
                        animate={{
                          width: "91%",
                        }}
                        transition={{
                          duration: 1.3,
                          delay: 0.2,
                          ease: "easeOut",
                        }}
                        className="
                          relative
                          h-full
                          rounded-full
                          bg-gradient-to-r
                          from-[#59b5cd]
                          via-[#ecb32c]
                          to-[#CF385D]
                        "
                      />

                    </div>

                  </div>


                  {/* CTA */}
                  <motion.button
                    type="button"
                    whileHover={{
                      scale: 1.015,
                      y: -2,
                    }}
                    whileTap={{
                      scale: 0.98,
                    }}
                    onClick={goToWhatsApp}
                    className="
                      group
                      relative
                      mt-6
                      flex
                      w-full
                      items-center
                      justify-center
                      gap-3
                      overflow-hidden
                      rounded-2xl
                      bg-[#111]
                      px-5
                      py-4
                      text-sm
                      font-bold
                      text-white
                      shadow-[0_15px_35px_rgba(0,0,0,.15)]
                      transition-all
                      duration-300
                      hover:bg-[#25D366]
                    "
                  >

                    {/* shine */}
                    <motion.span
                      initial={{
                        x: "-120%",
                      }}
                      whileHover={{
                        x: "120%",
                      }}
                      transition={{
                        duration: 0.7,
                      }}
                      className="
                        absolute
                        inset-y-0
                        w-1/3
                        skew-x-[-20deg]
                        bg-white/10
                      "
                    />

                    <FaWhatsapp className="relative text-lg" />

                    <span className="relative">
                      Talk to a Zulfah Advisor
                    </span>

                    <FaArrowRight
                      className="
                        relative
                        text-xs
                        transition
                        duration-300
                        group-hover:translate-x-1
                      "
                    />

                  </motion.button>


                  {/* RETAKE */}
                  <button
                    type="button"
                    onClick={openAdvisor}
                    className="
                      mt-3
                      w-full
                      py-2
                      text-[9px]
                      font-medium
                      uppercase
                      tracking-[0.2em]
                      text-gray-400
                      transition
                      hover:text-gray-900
                    "
                  >
                    Retake assessment
                  </button>

                </div>

              </motion.div>

            )}

          </div>

        </div>


        {/* =========================================================
            QUESTION AREA
        ========================================================= */}

        {advisorStep < questions.length && (

          <div
            className="
              relative
              shrink-0
              border-t
              border-black/[0.06]
              bg-white
              px-5
              py-4
              shadow-[0_-15px_40px_rgba(0,0,0,.04)]
              sm:px-8
              sm:py-5
            "
          >

            {/* glow */}
            <div
              className="
                pointer-events-none
                absolute
                bottom-0
                left-1/2
                h-32
                w-96
                -translate-x-1/2
                rounded-full
                bg-[#59b5cd]/5
                blur-[70px]
              "
            />

            <div className="relative mx-auto max-w-[700px]">

              {/* QUESTION HEADER */}

              <div className="mb-4">

                <div className="flex items-center gap-2">

                  <span
                    className="
                      rounded-full
                      bg-[#59b5cd]/10
                      px-2.5
                      py-1
                      text-[8px]
                      font-black
                      uppercase
                      tracking-[0.2em]
                      text-[#398aa1]
                    "
                  >
                    Question {advisorStep + 1}
                  </span>

                  <span className="text-[9px] text-gray-300">
                    Choose one
                  </span>

                </div>

                <motion.h3
                  key={advisorStep}
                  initial={{
                    opacity: 0,
                    x: 10,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  className="
                    mt-2
                    font-[ubuntu]
                    text-base
                    font-bold
                    leading-snug
                    text-gray-950
                    sm:text-xl
                  "
                >
                  {questions[advisorStep].question}
                </motion.h3>

              </div>


              {/* OPTIONS */}

              <motion.div
                key={`options-${advisorStep}`}
                initial={{
                  opacity: 0,
                  y: 8,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                className="grid gap-2 sm:grid-cols-2"
              >

                {questions[advisorStep].options.map(
                  (option, index) => (

                    <motion.button
                      key={option}
                      type="button"
                      initial={{
                        opacity: 0,
                        y: 10,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      transition={{
                        delay: index * 0.055,
                      }}
                      whileHover={{
                        y: -3,
                        scale: 1.01,
                      }}
                      whileTap={{
                        scale: 0.975,
                      }}
                      onClick={() =>
                        handleAdvisorAnswer(option)
                      }
                      className="
                        group
                        relative
                        flex
                        min-h-[56px]
                        w-full
                        items-center
                        justify-between
                        overflow-hidden
                        rounded-2xl
                        border
                        border-black/[0.07]
                        bg-[#F8F9F8]
                        px-3
                        py-3
                        text-left
                        transition-all
                        duration-300
                        hover:border-[#59b5cd]/40
                        hover:bg-[#59b5cd]/[0.06]
                        hover:shadow-[0_12px_35px_rgba(89,181,205,.12)]
                      "
                    >

                      {/* hover glow */}
                      <span
                        className="
                          pointer-events-none
                          absolute
                          inset-0
                          bg-gradient-to-r
                          from-[#59b5cd]/0
                          via-[#59b5cd]/[0.04]
                          to-[#ecb32c]/0
                          opacity-0
                          transition
                          group-hover:opacity-100
                        "
                      />

                      <div className="relative flex items-center gap-3">

                        <span
                          className="
                            flex
                            h-8
                            w-8
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            bg-white
                            text-[9px]
                            font-black
                            text-gray-300
                            shadow-sm
                            transition-all
                            duration-300
                            group-hover:bg-[#59b5cd]
                            group-hover:text-white
                            group-hover:shadow-[0_5px_15px_rgba(89,181,205,.25)]
                          "
                        >
                          {String.fromCharCode(65 + index)}
                        </span>

                        <span
                          className="
                            text-xs
                            font-medium
                            text-gray-600
                            transition
                            group-hover:text-gray-950
                            sm:text-sm
                          "
                        >
                          {option}
                        </span>

                      </div>

                      <span
                        className="
                          relative
                          flex
                          h-7
                          w-7
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          text-gray-300
                          transition-all
                          duration-300
                          group-hover:bg-[#ecb32c]
                          group-hover:text-white
                        "
                      >
                        <FaArrowRight
                          className="
                            text-[9px]
                            transition
                            group-hover:translate-x-0.5
                          "
                        />
                      </span>

                    </motion.button>

                  )
                )}

              </motion.div>


              {/* FOOTER HINT */}

              <div className="mt-3 flex items-center justify-center gap-2">

                <span className="h-1 w-1 rounded-full bg-[#59b5cd]" />

                <p className="text-[8px] text-gray-300 sm:text-[9px]">
                  Takes less than a minute · No commitment
                </p>

                <span className="h-1 w-1 rounded-full bg-[#ecb32c]" />

              </div>

            </div>

          </div>

        )}

      </motion.div>

    </motion.div>
  )}
</AnimatePresence>



    </Layout>
  );
};

export default Courses;